/* eslint-disable */
import { useMutation, useQuery } from '@tanstack/react-query';
import type { QueryKey, UseMutationOptions, UseQueryOptions } from '@tanstack/react-query';

import { studyAgentFetcher } from '../Fetcher';
import type { RequestOptions } from '../Fetcher';
const fetcher = studyAgentFetcher;

export type Maybe<T> = T | null;

export type OneOf<T> = T extends unknown[]
  ? T[number]
  : T extends readonly unknown[]
    ? T[number]
    : T;

export type Exact<T extends { [key: string]: unknown }> = {
  [K in keyof T]-?: Exclude<T[K], undefined>;
};

export type Values<T extends Record<string, unknown>> = T extends Record<string, infer U>
  ? U
  : T extends { [key: string]: infer U }
    ? U
    : never;

export const enum ResponseStatus {
  OK = 'OK',
  SERVER_ERROR = 'SERVER_ERROR',
  DUPLICATE = 'DUPLICATE',
  NOT_FOUND = 'NOT_FOUND',
  BAD_REQUEST = 'BAD_REQUEST',
  BAD_PARAMETER = 'BAD_PARAMETER',
  CONFLICT = 'CONFLICT',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  PAYLOAD_TOO_LARGE = 'PAYLOAD_TOO_LARGE',
  TOO_MANY_REQUESTS = 'TOO_MANY_REQUESTS',
}

export type Success<T = unknown> = {
  statusCode: ResponseStatus.OK;
  message: '';
  data: T;
  errorCode: string;
};

export type Error = {
  statusCode: Omit<keyof typeof ResponseStatus, 'OK'>;
  message: string;
  data: Maybe<''>;
  errorCode: string;
};

/**
 * @typeParam T - Response data type
 */
export type ApiResponse<T = unknown> = Success<T>;

export const API_ENDPOINT = {
  CLIENT_V1_MOCK_INTERVIEW_ORDERS_CANCEL: '/client/api/v1/mock-interview-orders/cancel',
  CLIENT_V1_MOCK_INTERVIEWS: '/client/api/v1/mock-interviews',
  CLIENT_V1_MOCK_INTERVIEWS_NO_LIMIT: '/client/api/v1/mock-interviews/no-limit',
  CLIENT_V1_MOCK_INTERVIEWS_FREE: '/client/api/v1/mock-interviews/free',
  CLIENT_V1_MOCK_INTERVIEW_VOUCHER_FREE: '/client/api/v1/mock-interview-voucher/free',
  CLIENT_V1_MOCK_INTERVIEW_ORDERS: '/client/api/v1/mock-interview-orders',
  CLIENT_V2_MOCK_INTERVIEW_VOUCHER: '/client/api/v2/mock-interview-voucher',
  CLIENT_V1_MOCK_INTERVIEWS_TYPES: '/client/api/v1/mock-interviews/types',
  CLIENT_V1_MOCK_INTERVIEW_VOUCHER: '/client/api/v1/mock-interview-voucher',
  CLIENT_V1_MOCK_INTERVIEW_VOUCHER_USER_TYPE: '/client/api/v1/mock-interview-voucher/user-type',
  CLIENT_V1_MOCK_INTERVIEW_VOUCHER_SUMMARY: '/client/api/v1/mock-interview-voucher/summary',
  CLIENT_V1_MOCK_INTERVIEW_PRODUCT: '/client/api/v1/mock-interview-product',
  CLIENT_V1_MOCK_INTERVIEW_ORDERS_ORDERID: (orderId: number) =>
    `/client/api/v1/mock-interview-orders/${orderId}`,
};

export function useClientApiV1MockInterviewOrdersCancelUpdateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1MockInterviewOrdersCancelUpdateResponse,
    unknown,
    ClientApiV1MockInterviewOrdersCancelUpdateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1MockInterviewOrdersCancelUpdateResponse,
    unknown,
    ClientApiV1MockInterviewOrdersCancelUpdateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1MockInterviewOrdersCancelUpdateVariables) =>
      fetcher.put(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MockInterviewOrdersCancelUpdateVariables = CancelMockInterviewOrderRequest;

export type ClientApiV1MockInterviewOrdersCancelUpdateResponse = ApiResponseUnit;

export function useClientApiV1MockInterviewsCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1MockInterviewsCreateResponse,
    unknown,
    ClientApiV1MockInterviewsCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1MockInterviewsCreateResponse,
    unknown,
    ClientApiV1MockInterviewsCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1MockInterviewsCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MockInterviewsCreateVariables = CreateMockInterviewRequest;

export type ClientApiV1MockInterviewsCreateResponse =
  ApiResponseCreateMockInterviewByVoucherResponse;

export function useClientApiV1MockInterviewsNoLimitCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1MockInterviewsNoLimitCreateResponse,
    unknown,
    ClientApiV1MockInterviewsNoLimitCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1MockInterviewsNoLimitCreateResponse,
    unknown,
    ClientApiV1MockInterviewsNoLimitCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1MockInterviewsNoLimitCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MockInterviewsNoLimitCreateResponse =
  ApiResponseCreateMockInterviewByVoucherResponse;

export type ClientApiV1MockInterviewsNoLimitCreateVariables = unknown;

export function useClientApiV1MockInterviewsFreeCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1MockInterviewsFreeCreateResponse,
    unknown,
    ClientApiV1MockInterviewsFreeCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1MockInterviewsFreeCreateResponse,
    unknown,
    ClientApiV1MockInterviewsFreeCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1MockInterviewsFreeCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MockInterviewsFreeCreateResponse =
  ApiResponseCreateMockInterviewByVoucherResponse;

export type ClientApiV1MockInterviewsFreeCreateVariables = unknown;

export function useClientApiV1MockInterviewVoucherFreeCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1MockInterviewVoucherFreeCreateResponse,
    unknown,
    ClientApiV1MockInterviewVoucherFreeCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1MockInterviewVoucherFreeCreateResponse,
    unknown,
    ClientApiV1MockInterviewVoucherFreeCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1MockInterviewVoucherFreeCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MockInterviewVoucherFreeCreateResponse = ApiResponseUnit;

export type ClientApiV1MockInterviewVoucherFreeCreateVariables = unknown;

export function useClientApiV1MockInterviewOrdersCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1MockInterviewOrdersCreateResponse,
    unknown,
    ClientApiV1MockInterviewOrdersCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1MockInterviewOrdersCreateResponse,
    unknown,
    ClientApiV1MockInterviewOrdersCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1MockInterviewOrdersCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MockInterviewOrdersCreateVariables = CreateMockInterviewOrderRequest;

export type ClientApiV1MockInterviewOrdersCreateResponse =
  ApiResponseCreateMockInterviewOrderResponse;

export const fetchClientApiV2MockInterviewVoucher = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV2MockInterviewVoucherResponse>(url, requestOptions);

export interface ClientApiV2MockInterviewVoucherQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV2MockInterviewVoucherResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV2MockInterviewVoucherQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV2MockInterviewVoucherQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV2MockInterviewVoucher(url, requestOptions),
  ...options,
});

export function useClientApiV2MockInterviewVoucherQuery(
  params: ClientApiV2MockInterviewVoucherQueryParams,
) {
  const result = useQuery<ClientApiV2MockInterviewVoucherResponse, unknown>(
    getClientApiV2MockInterviewVoucherQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/**
 *
 *             로그인한 사용자가 보유한 모의면접 이용권 목록을 id 내림차순으로 페이지네이션해 조회한다.
 *             이용권마다 연결된 모의면접의 진행 상태를 함께 판정해 status 로 내려주며, status 로 필터링할 수 있다.
 *             NO_LIMIT 타입 이용권도 조회 결과에 포함된다.
 *
 *             **status**
 *             - `CANCELLED`: 환불되었거나, 사용하지 않은 채 만료된 이용권
 *             - `UNUSED`: 아직 모의면접에 입장하지 않은 이용권. 모의면접을 만들어 둔 상태도 포함한다
 *             - `COMPLETED`: 모의면접 리포트까지 만들어진 이용권
 *             - `PROCESSING`: 그 외. 모의면접을 진행 중이거나 리포트를 만들고 있다
 *             - `ALL`: 상태로 거르지 않는다(기본값)
 *
 *             사용하지 않은 채 만료된 이용권만 CANCELLED 로 보고, 이미 입장한 이용권은 만료 시각이 지나도 진행 상태를 유지한다.
 *             만료 시각은 expiredAtUTC 로 내려준다.
 *             환불된 이용권의 환불 시각은 refundedAtUTC 로, 환불되지 않았으면 null 이다.
 *             면접 입장/종료 시각은 mockInterview.joinedAtUTC / mockInterview.leftAtUTC 로,
 *             아직 입장하거나 끝내지 않았으면 null 이다.
 *
 */
export type ClientApiV2MockInterviewVoucherQueryString = Partial<{
  status: 'ALL' | 'PROCESSING' | 'COMPLETED' | 'UNUSED' | 'CANCELLED';
  pageNumber: number;
  pageSize: number;
}>;

export type ClientApiV2MockInterviewVoucherResponse = ApiResponseGetMockInterviewVouchersResponseV2;

export const fetchClientApiV1MockInterviewsTypes = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1MockInterviewsTypesResponse>(url, requestOptions);

export interface ClientApiV1MockInterviewsTypesQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1MockInterviewsTypesResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1MockInterviewsTypesQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1MockInterviewsTypesQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1MockInterviewsTypes(url, requestOptions),
  ...options,
});

export function useClientApiV1MockInterviewsTypesQuery(
  params: ClientApiV1MockInterviewsTypesQueryParams,
) {
  const result = useQuery<ClientApiV1MockInterviewsTypesResponse, unknown>(
    getClientApiV1MockInterviewsTypesQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MockInterviewsTypesResponse = ApiResponseGetMockInterviewTypesResponse;

export const fetchClientApiV1MockInterviewVoucher = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1MockInterviewVoucherResponse>(url, requestOptions);

export interface ClientApiV1MockInterviewVoucherQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1MockInterviewVoucherResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1MockInterviewVoucherQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1MockInterviewVoucherQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1MockInterviewVoucher(url, requestOptions),
  ...options,
});

export function useClientApiV1MockInterviewVoucherQuery(
  params: ClientApiV1MockInterviewVoucherQueryParams,
) {
  const result = useQuery<ClientApiV1MockInterviewVoucherResponse, unknown>(
    getClientApiV1MockInterviewVoucherQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/**
 *
 *             로그인한 사용자가 보유한 모의면접 이용권 목록을 id 내림차순으로 조회한다. isUsed 로 사용 여부를 필터링할 수 있다.
 *             NO_LIMIT 타입 이용권은 조회 결과에서 제외된다.
 *
 */
export type ClientApiV1MockInterviewVoucherQueryString = Partial<{
  isUsed: boolean;
}>;

export type ClientApiV1MockInterviewVoucherResponse = ApiResponseGetMockInterviewVouchersResponse;

export const fetchClientApiV1MockInterviewVoucherUserType = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1MockInterviewVoucherUserTypeResponse>(url, requestOptions);

export interface ClientApiV1MockInterviewVoucherUserTypeQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1MockInterviewVoucherUserTypeResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1MockInterviewVoucherUserTypeQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1MockInterviewVoucherUserTypeQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1MockInterviewVoucherUserType(url, requestOptions),
  ...options,
});

export function useClientApiV1MockInterviewVoucherUserTypeQuery(
  params: ClientApiV1MockInterviewVoucherUserTypeQueryParams,
) {
  const result = useQuery<ClientApiV1MockInterviewVoucherUserTypeResponse, unknown>(
    getClientApiV1MockInterviewVoucherUserTypeQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MockInterviewVoucherUserTypeResponse =
  ApiResponseGetMockInterviewVoucherUserTypeResponse;

export const fetchClientApiV1MockInterviewVoucherSummary = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1MockInterviewVoucherSummaryResponse>(url, requestOptions);

export interface ClientApiV1MockInterviewVoucherSummaryQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1MockInterviewVoucherSummaryResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1MockInterviewVoucherSummaryQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1MockInterviewVoucherSummaryQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1MockInterviewVoucherSummary(url, requestOptions),
  ...options,
});

export function useClientApiV1MockInterviewVoucherSummaryQuery(
  params: ClientApiV1MockInterviewVoucherSummaryQueryParams,
) {
  const result = useQuery<ClientApiV1MockInterviewVoucherSummaryResponse, unknown>(
    getClientApiV1MockInterviewVoucherSummaryQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MockInterviewVoucherSummaryResponse =
  ApiResponseGetMockInterviewVoucherSummaryResponse;

export const fetchClientApiV1MockInterviewProduct = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1MockInterviewProductResponse>(url, requestOptions);

export interface ClientApiV1MockInterviewProductQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1MockInterviewProductResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1MockInterviewProductQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1MockInterviewProductQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1MockInterviewProduct(url, requestOptions),
  ...options,
});

export function useClientApiV1MockInterviewProductQuery(
  params: ClientApiV1MockInterviewProductQueryParams,
) {
  const result = useQuery<ClientApiV1MockInterviewProductResponse, unknown>(
    getClientApiV1MockInterviewProductQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/**
 *
 *             삭제되지 않은 모의면접 상품 목록을 id 오름차순으로 조회한다. 로그인하지 않아도 조회할 수 있다.
 *             productCode 를 지정하면 해당 상품만 조회하며, 존재하지 않으면 빈 목록을 반환한다.
 *             진행 중인 할인이 있으면 paymentPrice 에 가장 저렴한 할인가가 적용되며, 할인이 없으면 regularPrice 와 같다.
 *
 */
export type ClientApiV1MockInterviewProductQueryString = Partial<{
  productCode: 'ONE_TIME' | 'FREE_ONE_TIME';
}>;

export type ClientApiV1MockInterviewProductResponse = ApiResponseGetMockInterviewProductsResponse;

export const fetchClientApiV1MockInterviewOrdersOrderId = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1MockInterviewOrdersOrderIdResponse>(url, requestOptions);

export interface ClientApiV1MockInterviewOrdersOrderIdQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1MockInterviewOrdersOrderIdResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1MockInterviewOrdersOrderIdQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1MockInterviewOrdersOrderIdQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1MockInterviewOrdersOrderId(url, requestOptions),
  ...options,
});

export function useClientApiV1MockInterviewOrdersOrderIdQuery(
  params: ClientApiV1MockInterviewOrdersOrderIdQueryParams,
) {
  const result = useQuery<ClientApiV1MockInterviewOrdersOrderIdResponse, unknown>(
    getClientApiV1MockInterviewOrdersOrderIdQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export type ClientApiV1MockInterviewOrdersOrderIdResponse =
  ApiResponseGetMockInterviewOrderResponse;

export interface ApiResponseUnit {
  statusCode:
    | '100 CONTINUE'
    | '101 SWITCHING_PROTOCOLS'
    | '102 PROCESSING'
    | '103 EARLY_HINTS'
    | '200 OK'
    | '201 CREATED'
    | '202 ACCEPTED'
    | '203 NON_AUTHORITATIVE_INFORMATION'
    | '204 NO_CONTENT'
    | '205 RESET_CONTENT'
    | '206 PARTIAL_CONTENT'
    | '207 MULTI_STATUS'
    | '208 ALREADY_REPORTED'
    | '226 IM_USED'
    | '300 MULTIPLE_CHOICES'
    | '301 MOVED_PERMANENTLY'
    | '302 FOUND'
    | '303 SEE_OTHER'
    | '304 NOT_MODIFIED'
    | '307 TEMPORARY_REDIRECT'
    | '308 PERMANENT_REDIRECT'
    | '400 BAD_REQUEST'
    | '401 UNAUTHORIZED'
    | '402 PAYMENT_REQUIRED'
    | '403 FORBIDDEN'
    | '404 NOT_FOUND'
    | '405 METHOD_NOT_ALLOWED'
    | '406 NOT_ACCEPTABLE'
    | '407 PROXY_AUTHENTICATION_REQUIRED'
    | '408 REQUEST_TIMEOUT'
    | '409 CONFLICT'
    | '410 GONE'
    | '411 LENGTH_REQUIRED'
    | '412 PRECONDITION_FAILED'
    | '413 CONTENT_TOO_LARGE'
    | '413 PAYLOAD_TOO_LARGE'
    | '414 URI_TOO_LONG'
    | '415 UNSUPPORTED_MEDIA_TYPE'
    | '416 REQUESTED_RANGE_NOT_SATISFIABLE'
    | '417 EXPECTATION_FAILED'
    | '418 I_AM_A_TEAPOT'
    | '421 MISDIRECTED_REQUEST'
    | '422 UNPROCESSABLE_CONTENT'
    | '422 UNPROCESSABLE_ENTITY'
    | '423 LOCKED'
    | '424 FAILED_DEPENDENCY'
    | '425 TOO_EARLY'
    | '426 UPGRADE_REQUIRED'
    | '428 PRECONDITION_REQUIRED'
    | '429 TOO_MANY_REQUESTS'
    | '431 REQUEST_HEADER_FIELDS_TOO_LARGE'
    | '451 UNAVAILABLE_FOR_LEGAL_REASONS'
    | '500 INTERNAL_SERVER_ERROR'
    | '501 NOT_IMPLEMENTED'
    | '502 BAD_GATEWAY'
    | '503 SERVICE_UNAVAILABLE'
    | '504 GATEWAY_TIMEOUT'
    | '505 HTTP_VERSION_NOT_SUPPORTED'
    | '506 VARIANT_ALSO_NEGOTIATES'
    | '507 INSUFFICIENT_STORAGE'
    | '508 LOOP_DETECTED'
    | '509 BANDWIDTH_LIMIT_EXCEEDED'
    | '510 NOT_EXTENDED'
    | '511 NETWORK_AUTHENTICATION_REQUIRED';
  message: string;
  errorCode?: string;
  data: unknown;
}

export interface CancelMockInterviewOrderRequest {
  orderId: number;
  reasonType: 'PAYMENT_METHOD_CHANGE' | 'DISSATISFACTION' | 'DISCOUNT' | 'CHANGE_MIND' | 'ETC';
  reasonMessage?: string;
}

export interface CreateMockInterviewRequest {
  mockInterviewVoucherId: number;
}

export interface ApiResponseCreateMockInterviewByVoucherResponse {
  statusCode:
    | '100 CONTINUE'
    | '101 SWITCHING_PROTOCOLS'
    | '102 PROCESSING'
    | '103 EARLY_HINTS'
    | '200 OK'
    | '201 CREATED'
    | '202 ACCEPTED'
    | '203 NON_AUTHORITATIVE_INFORMATION'
    | '204 NO_CONTENT'
    | '205 RESET_CONTENT'
    | '206 PARTIAL_CONTENT'
    | '207 MULTI_STATUS'
    | '208 ALREADY_REPORTED'
    | '226 IM_USED'
    | '300 MULTIPLE_CHOICES'
    | '301 MOVED_PERMANENTLY'
    | '302 FOUND'
    | '303 SEE_OTHER'
    | '304 NOT_MODIFIED'
    | '307 TEMPORARY_REDIRECT'
    | '308 PERMANENT_REDIRECT'
    | '400 BAD_REQUEST'
    | '401 UNAUTHORIZED'
    | '402 PAYMENT_REQUIRED'
    | '403 FORBIDDEN'
    | '404 NOT_FOUND'
    | '405 METHOD_NOT_ALLOWED'
    | '406 NOT_ACCEPTABLE'
    | '407 PROXY_AUTHENTICATION_REQUIRED'
    | '408 REQUEST_TIMEOUT'
    | '409 CONFLICT'
    | '410 GONE'
    | '411 LENGTH_REQUIRED'
    | '412 PRECONDITION_FAILED'
    | '413 CONTENT_TOO_LARGE'
    | '413 PAYLOAD_TOO_LARGE'
    | '414 URI_TOO_LONG'
    | '415 UNSUPPORTED_MEDIA_TYPE'
    | '416 REQUESTED_RANGE_NOT_SATISFIABLE'
    | '417 EXPECTATION_FAILED'
    | '418 I_AM_A_TEAPOT'
    | '421 MISDIRECTED_REQUEST'
    | '422 UNPROCESSABLE_CONTENT'
    | '422 UNPROCESSABLE_ENTITY'
    | '423 LOCKED'
    | '424 FAILED_DEPENDENCY'
    | '425 TOO_EARLY'
    | '426 UPGRADE_REQUIRED'
    | '428 PRECONDITION_REQUIRED'
    | '429 TOO_MANY_REQUESTS'
    | '431 REQUEST_HEADER_FIELDS_TOO_LARGE'
    | '451 UNAVAILABLE_FOR_LEGAL_REASONS'
    | '500 INTERNAL_SERVER_ERROR'
    | '501 NOT_IMPLEMENTED'
    | '502 BAD_GATEWAY'
    | '503 SERVICE_UNAVAILABLE'
    | '504 GATEWAY_TIMEOUT'
    | '505 HTTP_VERSION_NOT_SUPPORTED'
    | '506 VARIANT_ALSO_NEGOTIATES'
    | '507 INSUFFICIENT_STORAGE'
    | '508 LOOP_DETECTED'
    | '509 BANDWIDTH_LIMIT_EXCEEDED'
    | '510 NOT_EXTENDED'
    | '511 NETWORK_AUTHENTICATION_REQUIRED';
  message: string;
  errorCode?: string;
  data: CreateMockInterviewByVoucherResponse;
}

export interface CreateMockInterviewByVoucherResponse {
  mockInterviewId: number;
}

export interface BuyerRequest {
  name: string;
  email: string;
  phoneNumber: string;
  countryCode: string;
}

export interface CreateMockInterviewOrderRequest {
  entryAtUTC: string;
  entryUrl: string;
  buyer: BuyerRequest;
  payMetadata: PayMetadataRequest;
  product: ProductRequest;
  mockInterviewId?: number;
}

export interface PayMetadataRequest {
  payPgType: 'TOSS';
  payMethod:
    | 'APPLEPAY'
    | 'BANK_TRANSFER'
    | 'CARD'
    | 'INFPAY'
    | 'KAKAOPAY'
    | 'NAVERPAY'
    | 'TOSSPAY'
    | 'PAYCO';
}

export interface ProductRequest {
  id: number;
  regularPrice: number;
  paymentPrice: number;
}

export interface ApiResponseCreateMockInterviewOrderResponse {
  statusCode:
    | '100 CONTINUE'
    | '101 SWITCHING_PROTOCOLS'
    | '102 PROCESSING'
    | '103 EARLY_HINTS'
    | '200 OK'
    | '201 CREATED'
    | '202 ACCEPTED'
    | '203 NON_AUTHORITATIVE_INFORMATION'
    | '204 NO_CONTENT'
    | '205 RESET_CONTENT'
    | '206 PARTIAL_CONTENT'
    | '207 MULTI_STATUS'
    | '208 ALREADY_REPORTED'
    | '226 IM_USED'
    | '300 MULTIPLE_CHOICES'
    | '301 MOVED_PERMANENTLY'
    | '302 FOUND'
    | '303 SEE_OTHER'
    | '304 NOT_MODIFIED'
    | '307 TEMPORARY_REDIRECT'
    | '308 PERMANENT_REDIRECT'
    | '400 BAD_REQUEST'
    | '401 UNAUTHORIZED'
    | '402 PAYMENT_REQUIRED'
    | '403 FORBIDDEN'
    | '404 NOT_FOUND'
    | '405 METHOD_NOT_ALLOWED'
    | '406 NOT_ACCEPTABLE'
    | '407 PROXY_AUTHENTICATION_REQUIRED'
    | '408 REQUEST_TIMEOUT'
    | '409 CONFLICT'
    | '410 GONE'
    | '411 LENGTH_REQUIRED'
    | '412 PRECONDITION_FAILED'
    | '413 CONTENT_TOO_LARGE'
    | '413 PAYLOAD_TOO_LARGE'
    | '414 URI_TOO_LONG'
    | '415 UNSUPPORTED_MEDIA_TYPE'
    | '416 REQUESTED_RANGE_NOT_SATISFIABLE'
    | '417 EXPECTATION_FAILED'
    | '418 I_AM_A_TEAPOT'
    | '421 MISDIRECTED_REQUEST'
    | '422 UNPROCESSABLE_CONTENT'
    | '422 UNPROCESSABLE_ENTITY'
    | '423 LOCKED'
    | '424 FAILED_DEPENDENCY'
    | '425 TOO_EARLY'
    | '426 UPGRADE_REQUIRED'
    | '428 PRECONDITION_REQUIRED'
    | '429 TOO_MANY_REQUESTS'
    | '431 REQUEST_HEADER_FIELDS_TOO_LARGE'
    | '451 UNAVAILABLE_FOR_LEGAL_REASONS'
    | '500 INTERNAL_SERVER_ERROR'
    | '501 NOT_IMPLEMENTED'
    | '502 BAD_GATEWAY'
    | '503 SERVICE_UNAVAILABLE'
    | '504 GATEWAY_TIMEOUT'
    | '505 HTTP_VERSION_NOT_SUPPORTED'
    | '506 VARIANT_ALSO_NEGOTIATES'
    | '507 INSUFFICIENT_STORAGE'
    | '508 LOOP_DETECTED'
    | '509 BANDWIDTH_LIMIT_EXCEEDED'
    | '510 NOT_EXTENDED'
    | '511 NETWORK_AUTHENTICATION_REQUIRED';
  message: string;
  errorCode?: string;
  data: CreateMockInterviewOrderResponse;
}

export interface BuyerResponse {
  name: string;
  email: string;
  phoneNumber: string;
  countryCode: string;
}

export interface CreateMockInterviewOrderResponse {
  id: number;
  merchantUid: string;
  title: string;
  buyer: BuyerResponse;
  returnUrl: string;
}

export interface ApiResponseGetMockInterviewVouchersResponseV2 {
  statusCode:
    | '100 CONTINUE'
    | '101 SWITCHING_PROTOCOLS'
    | '102 PROCESSING'
    | '103 EARLY_HINTS'
    | '200 OK'
    | '201 CREATED'
    | '202 ACCEPTED'
    | '203 NON_AUTHORITATIVE_INFORMATION'
    | '204 NO_CONTENT'
    | '205 RESET_CONTENT'
    | '206 PARTIAL_CONTENT'
    | '207 MULTI_STATUS'
    | '208 ALREADY_REPORTED'
    | '226 IM_USED'
    | '300 MULTIPLE_CHOICES'
    | '301 MOVED_PERMANENTLY'
    | '302 FOUND'
    | '303 SEE_OTHER'
    | '304 NOT_MODIFIED'
    | '307 TEMPORARY_REDIRECT'
    | '308 PERMANENT_REDIRECT'
    | '400 BAD_REQUEST'
    | '401 UNAUTHORIZED'
    | '402 PAYMENT_REQUIRED'
    | '403 FORBIDDEN'
    | '404 NOT_FOUND'
    | '405 METHOD_NOT_ALLOWED'
    | '406 NOT_ACCEPTABLE'
    | '407 PROXY_AUTHENTICATION_REQUIRED'
    | '408 REQUEST_TIMEOUT'
    | '409 CONFLICT'
    | '410 GONE'
    | '411 LENGTH_REQUIRED'
    | '412 PRECONDITION_FAILED'
    | '413 CONTENT_TOO_LARGE'
    | '413 PAYLOAD_TOO_LARGE'
    | '414 URI_TOO_LONG'
    | '415 UNSUPPORTED_MEDIA_TYPE'
    | '416 REQUESTED_RANGE_NOT_SATISFIABLE'
    | '417 EXPECTATION_FAILED'
    | '418 I_AM_A_TEAPOT'
    | '421 MISDIRECTED_REQUEST'
    | '422 UNPROCESSABLE_CONTENT'
    | '422 UNPROCESSABLE_ENTITY'
    | '423 LOCKED'
    | '424 FAILED_DEPENDENCY'
    | '425 TOO_EARLY'
    | '426 UPGRADE_REQUIRED'
    | '428 PRECONDITION_REQUIRED'
    | '429 TOO_MANY_REQUESTS'
    | '431 REQUEST_HEADER_FIELDS_TOO_LARGE'
    | '451 UNAVAILABLE_FOR_LEGAL_REASONS'
    | '500 INTERNAL_SERVER_ERROR'
    | '501 NOT_IMPLEMENTED'
    | '502 BAD_GATEWAY'
    | '503 SERVICE_UNAVAILABLE'
    | '504 GATEWAY_TIMEOUT'
    | '505 HTTP_VERSION_NOT_SUPPORTED'
    | '506 VARIANT_ALSO_NEGOTIATES'
    | '507 INSUFFICIENT_STORAGE'
    | '508 LOOP_DETECTED'
    | '509 BANDWIDTH_LIMIT_EXCEEDED'
    | '510 NOT_EXTENDED'
    | '511 NETWORK_AUTHENTICATION_REQUIRED';
  message: string;
  errorCode?: string;
  data: GetMockInterviewVouchersResponseV2;
}

export interface GetMockInterviewVouchersResponseV2 {
  totalPage: number;
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: MockInterviewVoucherItem[];
}

export interface MockInterviewProductResponse {
  title: string;
}

export interface MockInterviewResponse {
  mockInterviewId: number;
  status?:
    | 'INITIALIZED'
    | 'SUBMIT'
    | 'PREPARING'
    | 'READY'
    | 'ON_AIR'
    | 'FINISHED'
    | 'REPORT_GENERATING'
    | 'REPORT_GENERATED';
  interviewerPersonaType?:
    | 'DEFAULT'
    | 'CALM_ANALYST'
    | 'RELAXED_EXECUTIVE'
    | 'CURIOUS_LEAD'
    | 'DIRECT_PRACTITIONER'
    | 'HYANGRO';
  joinedAtUTC?: string;
  leftAtUTC?: string;
}

export interface MockInterviewVoucherItem {
  mockInterviewVoucherId: number;
  status: 'PROCESSING' | 'COMPLETED' | 'UNUSED' | 'CANCELLED';
  type: 'FREE' | 'NO_LIMIT' | 'PAID';
  expiredAtUTC?: string;
  usedAtUTC?: string;
  createdAtUTC: string;
  refundedAtUTC?: string;
  mockInterview?: MockInterviewResponse;
  mockInterviewProduct: MockInterviewProductResponse;
}

export interface ApiResponseGetMockInterviewTypesResponse {
  statusCode:
    | '100 CONTINUE'
    | '101 SWITCHING_PROTOCOLS'
    | '102 PROCESSING'
    | '103 EARLY_HINTS'
    | '200 OK'
    | '201 CREATED'
    | '202 ACCEPTED'
    | '203 NON_AUTHORITATIVE_INFORMATION'
    | '204 NO_CONTENT'
    | '205 RESET_CONTENT'
    | '206 PARTIAL_CONTENT'
    | '207 MULTI_STATUS'
    | '208 ALREADY_REPORTED'
    | '226 IM_USED'
    | '300 MULTIPLE_CHOICES'
    | '301 MOVED_PERMANENTLY'
    | '302 FOUND'
    | '303 SEE_OTHER'
    | '304 NOT_MODIFIED'
    | '307 TEMPORARY_REDIRECT'
    | '308 PERMANENT_REDIRECT'
    | '400 BAD_REQUEST'
    | '401 UNAUTHORIZED'
    | '402 PAYMENT_REQUIRED'
    | '403 FORBIDDEN'
    | '404 NOT_FOUND'
    | '405 METHOD_NOT_ALLOWED'
    | '406 NOT_ACCEPTABLE'
    | '407 PROXY_AUTHENTICATION_REQUIRED'
    | '408 REQUEST_TIMEOUT'
    | '409 CONFLICT'
    | '410 GONE'
    | '411 LENGTH_REQUIRED'
    | '412 PRECONDITION_FAILED'
    | '413 CONTENT_TOO_LARGE'
    | '413 PAYLOAD_TOO_LARGE'
    | '414 URI_TOO_LONG'
    | '415 UNSUPPORTED_MEDIA_TYPE'
    | '416 REQUESTED_RANGE_NOT_SATISFIABLE'
    | '417 EXPECTATION_FAILED'
    | '418 I_AM_A_TEAPOT'
    | '421 MISDIRECTED_REQUEST'
    | '422 UNPROCESSABLE_CONTENT'
    | '422 UNPROCESSABLE_ENTITY'
    | '423 LOCKED'
    | '424 FAILED_DEPENDENCY'
    | '425 TOO_EARLY'
    | '426 UPGRADE_REQUIRED'
    | '428 PRECONDITION_REQUIRED'
    | '429 TOO_MANY_REQUESTS'
    | '431 REQUEST_HEADER_FIELDS_TOO_LARGE'
    | '451 UNAVAILABLE_FOR_LEGAL_REASONS'
    | '500 INTERNAL_SERVER_ERROR'
    | '501 NOT_IMPLEMENTED'
    | '502 BAD_GATEWAY'
    | '503 SERVICE_UNAVAILABLE'
    | '504 GATEWAY_TIMEOUT'
    | '505 HTTP_VERSION_NOT_SUPPORTED'
    | '506 VARIANT_ALSO_NEGOTIATES'
    | '507 INSUFFICIENT_STORAGE'
    | '508 LOOP_DETECTED'
    | '509 BANDWIDTH_LIMIT_EXCEEDED'
    | '510 NOT_EXTENDED'
    | '511 NETWORK_AUTHENTICATION_REQUIRED';
  message: string;
  errorCode?: string;
  data: GetMockInterviewTypesResponse;
}

export interface GetMockInterviewTypeItemResponse {
  code: string;
  displayName: string;
}

export interface GetMockInterviewTypesResponse {
  items: GetMockInterviewTypeItemResponse[];
}

export interface ApiResponseGetMockInterviewVouchersResponse {
  statusCode:
    | '100 CONTINUE'
    | '101 SWITCHING_PROTOCOLS'
    | '102 PROCESSING'
    | '103 EARLY_HINTS'
    | '200 OK'
    | '201 CREATED'
    | '202 ACCEPTED'
    | '203 NON_AUTHORITATIVE_INFORMATION'
    | '204 NO_CONTENT'
    | '205 RESET_CONTENT'
    | '206 PARTIAL_CONTENT'
    | '207 MULTI_STATUS'
    | '208 ALREADY_REPORTED'
    | '226 IM_USED'
    | '300 MULTIPLE_CHOICES'
    | '301 MOVED_PERMANENTLY'
    | '302 FOUND'
    | '303 SEE_OTHER'
    | '304 NOT_MODIFIED'
    | '307 TEMPORARY_REDIRECT'
    | '308 PERMANENT_REDIRECT'
    | '400 BAD_REQUEST'
    | '401 UNAUTHORIZED'
    | '402 PAYMENT_REQUIRED'
    | '403 FORBIDDEN'
    | '404 NOT_FOUND'
    | '405 METHOD_NOT_ALLOWED'
    | '406 NOT_ACCEPTABLE'
    | '407 PROXY_AUTHENTICATION_REQUIRED'
    | '408 REQUEST_TIMEOUT'
    | '409 CONFLICT'
    | '410 GONE'
    | '411 LENGTH_REQUIRED'
    | '412 PRECONDITION_FAILED'
    | '413 CONTENT_TOO_LARGE'
    | '413 PAYLOAD_TOO_LARGE'
    | '414 URI_TOO_LONG'
    | '415 UNSUPPORTED_MEDIA_TYPE'
    | '416 REQUESTED_RANGE_NOT_SATISFIABLE'
    | '417 EXPECTATION_FAILED'
    | '418 I_AM_A_TEAPOT'
    | '421 MISDIRECTED_REQUEST'
    | '422 UNPROCESSABLE_CONTENT'
    | '422 UNPROCESSABLE_ENTITY'
    | '423 LOCKED'
    | '424 FAILED_DEPENDENCY'
    | '425 TOO_EARLY'
    | '426 UPGRADE_REQUIRED'
    | '428 PRECONDITION_REQUIRED'
    | '429 TOO_MANY_REQUESTS'
    | '431 REQUEST_HEADER_FIELDS_TOO_LARGE'
    | '451 UNAVAILABLE_FOR_LEGAL_REASONS'
    | '500 INTERNAL_SERVER_ERROR'
    | '501 NOT_IMPLEMENTED'
    | '502 BAD_GATEWAY'
    | '503 SERVICE_UNAVAILABLE'
    | '504 GATEWAY_TIMEOUT'
    | '505 HTTP_VERSION_NOT_SUPPORTED'
    | '506 VARIANT_ALSO_NEGOTIATES'
    | '507 INSUFFICIENT_STORAGE'
    | '508 LOOP_DETECTED'
    | '509 BANDWIDTH_LIMIT_EXCEEDED'
    | '510 NOT_EXTENDED'
    | '511 NETWORK_AUTHENTICATION_REQUIRED';
  message: string;
  errorCode?: string;
  data: GetMockInterviewVouchersResponse;
}

export interface GetMockInterviewVoucherItemResponse {
  id: number;
  type: 'FREE' | 'NO_LIMIT' | 'PAID';
  isUsed: boolean;
  usedAtUTC?: string;
  mockInterview?: MockInterviewResponse;
}

export interface GetMockInterviewVouchersResponse {
  items: GetMockInterviewVoucherItemResponse[];
}

export interface ApiResponseGetMockInterviewVoucherUserTypeResponse {
  statusCode:
    | '100 CONTINUE'
    | '101 SWITCHING_PROTOCOLS'
    | '102 PROCESSING'
    | '103 EARLY_HINTS'
    | '200 OK'
    | '201 CREATED'
    | '202 ACCEPTED'
    | '203 NON_AUTHORITATIVE_INFORMATION'
    | '204 NO_CONTENT'
    | '205 RESET_CONTENT'
    | '206 PARTIAL_CONTENT'
    | '207 MULTI_STATUS'
    | '208 ALREADY_REPORTED'
    | '226 IM_USED'
    | '300 MULTIPLE_CHOICES'
    | '301 MOVED_PERMANENTLY'
    | '302 FOUND'
    | '303 SEE_OTHER'
    | '304 NOT_MODIFIED'
    | '307 TEMPORARY_REDIRECT'
    | '308 PERMANENT_REDIRECT'
    | '400 BAD_REQUEST'
    | '401 UNAUTHORIZED'
    | '402 PAYMENT_REQUIRED'
    | '403 FORBIDDEN'
    | '404 NOT_FOUND'
    | '405 METHOD_NOT_ALLOWED'
    | '406 NOT_ACCEPTABLE'
    | '407 PROXY_AUTHENTICATION_REQUIRED'
    | '408 REQUEST_TIMEOUT'
    | '409 CONFLICT'
    | '410 GONE'
    | '411 LENGTH_REQUIRED'
    | '412 PRECONDITION_FAILED'
    | '413 CONTENT_TOO_LARGE'
    | '413 PAYLOAD_TOO_LARGE'
    | '414 URI_TOO_LONG'
    | '415 UNSUPPORTED_MEDIA_TYPE'
    | '416 REQUESTED_RANGE_NOT_SATISFIABLE'
    | '417 EXPECTATION_FAILED'
    | '418 I_AM_A_TEAPOT'
    | '421 MISDIRECTED_REQUEST'
    | '422 UNPROCESSABLE_CONTENT'
    | '422 UNPROCESSABLE_ENTITY'
    | '423 LOCKED'
    | '424 FAILED_DEPENDENCY'
    | '425 TOO_EARLY'
    | '426 UPGRADE_REQUIRED'
    | '428 PRECONDITION_REQUIRED'
    | '429 TOO_MANY_REQUESTS'
    | '431 REQUEST_HEADER_FIELDS_TOO_LARGE'
    | '451 UNAVAILABLE_FOR_LEGAL_REASONS'
    | '500 INTERNAL_SERVER_ERROR'
    | '501 NOT_IMPLEMENTED'
    | '502 BAD_GATEWAY'
    | '503 SERVICE_UNAVAILABLE'
    | '504 GATEWAY_TIMEOUT'
    | '505 HTTP_VERSION_NOT_SUPPORTED'
    | '506 VARIANT_ALSO_NEGOTIATES'
    | '507 INSUFFICIENT_STORAGE'
    | '508 LOOP_DETECTED'
    | '509 BANDWIDTH_LIMIT_EXCEEDED'
    | '510 NOT_EXTENDED'
    | '511 NETWORK_AUTHENTICATION_REQUIRED';
  message: string;
  errorCode?: string;
  data: GetMockInterviewVoucherUserTypeResponse;
}

export interface GetMockInterviewVoucherUserTypeResponse {
  type: 'NO_LIMIT' | 'FREE_AVAILABLE' | 'HAS_VOUCHER' | 'NO_VOUCHER';
}

export interface ApiResponseGetMockInterviewVoucherSummaryResponse {
  statusCode:
    | '100 CONTINUE'
    | '101 SWITCHING_PROTOCOLS'
    | '102 PROCESSING'
    | '103 EARLY_HINTS'
    | '200 OK'
    | '201 CREATED'
    | '202 ACCEPTED'
    | '203 NON_AUTHORITATIVE_INFORMATION'
    | '204 NO_CONTENT'
    | '205 RESET_CONTENT'
    | '206 PARTIAL_CONTENT'
    | '207 MULTI_STATUS'
    | '208 ALREADY_REPORTED'
    | '226 IM_USED'
    | '300 MULTIPLE_CHOICES'
    | '301 MOVED_PERMANENTLY'
    | '302 FOUND'
    | '303 SEE_OTHER'
    | '304 NOT_MODIFIED'
    | '307 TEMPORARY_REDIRECT'
    | '308 PERMANENT_REDIRECT'
    | '400 BAD_REQUEST'
    | '401 UNAUTHORIZED'
    | '402 PAYMENT_REQUIRED'
    | '403 FORBIDDEN'
    | '404 NOT_FOUND'
    | '405 METHOD_NOT_ALLOWED'
    | '406 NOT_ACCEPTABLE'
    | '407 PROXY_AUTHENTICATION_REQUIRED'
    | '408 REQUEST_TIMEOUT'
    | '409 CONFLICT'
    | '410 GONE'
    | '411 LENGTH_REQUIRED'
    | '412 PRECONDITION_FAILED'
    | '413 CONTENT_TOO_LARGE'
    | '413 PAYLOAD_TOO_LARGE'
    | '414 URI_TOO_LONG'
    | '415 UNSUPPORTED_MEDIA_TYPE'
    | '416 REQUESTED_RANGE_NOT_SATISFIABLE'
    | '417 EXPECTATION_FAILED'
    | '418 I_AM_A_TEAPOT'
    | '421 MISDIRECTED_REQUEST'
    | '422 UNPROCESSABLE_CONTENT'
    | '422 UNPROCESSABLE_ENTITY'
    | '423 LOCKED'
    | '424 FAILED_DEPENDENCY'
    | '425 TOO_EARLY'
    | '426 UPGRADE_REQUIRED'
    | '428 PRECONDITION_REQUIRED'
    | '429 TOO_MANY_REQUESTS'
    | '431 REQUEST_HEADER_FIELDS_TOO_LARGE'
    | '451 UNAVAILABLE_FOR_LEGAL_REASONS'
    | '500 INTERNAL_SERVER_ERROR'
    | '501 NOT_IMPLEMENTED'
    | '502 BAD_GATEWAY'
    | '503 SERVICE_UNAVAILABLE'
    | '504 GATEWAY_TIMEOUT'
    | '505 HTTP_VERSION_NOT_SUPPORTED'
    | '506 VARIANT_ALSO_NEGOTIATES'
    | '507 INSUFFICIENT_STORAGE'
    | '508 LOOP_DETECTED'
    | '509 BANDWIDTH_LIMIT_EXCEEDED'
    | '510 NOT_EXTENDED'
    | '511 NETWORK_AUTHENTICATION_REQUIRED';
  message: string;
  errorCode?: string;
  data: GetMockInterviewVoucherSummaryResponse;
}

export interface GetMockInterviewVoucherSummaryResponse {
  all: number;
  unused: number;
  processing: number;
  completed: number;
  cancelled: number;
}

export interface ApiResponseGetMockInterviewProductsResponse {
  statusCode:
    | '100 CONTINUE'
    | '101 SWITCHING_PROTOCOLS'
    | '102 PROCESSING'
    | '103 EARLY_HINTS'
    | '200 OK'
    | '201 CREATED'
    | '202 ACCEPTED'
    | '203 NON_AUTHORITATIVE_INFORMATION'
    | '204 NO_CONTENT'
    | '205 RESET_CONTENT'
    | '206 PARTIAL_CONTENT'
    | '207 MULTI_STATUS'
    | '208 ALREADY_REPORTED'
    | '226 IM_USED'
    | '300 MULTIPLE_CHOICES'
    | '301 MOVED_PERMANENTLY'
    | '302 FOUND'
    | '303 SEE_OTHER'
    | '304 NOT_MODIFIED'
    | '307 TEMPORARY_REDIRECT'
    | '308 PERMANENT_REDIRECT'
    | '400 BAD_REQUEST'
    | '401 UNAUTHORIZED'
    | '402 PAYMENT_REQUIRED'
    | '403 FORBIDDEN'
    | '404 NOT_FOUND'
    | '405 METHOD_NOT_ALLOWED'
    | '406 NOT_ACCEPTABLE'
    | '407 PROXY_AUTHENTICATION_REQUIRED'
    | '408 REQUEST_TIMEOUT'
    | '409 CONFLICT'
    | '410 GONE'
    | '411 LENGTH_REQUIRED'
    | '412 PRECONDITION_FAILED'
    | '413 CONTENT_TOO_LARGE'
    | '413 PAYLOAD_TOO_LARGE'
    | '414 URI_TOO_LONG'
    | '415 UNSUPPORTED_MEDIA_TYPE'
    | '416 REQUESTED_RANGE_NOT_SATISFIABLE'
    | '417 EXPECTATION_FAILED'
    | '418 I_AM_A_TEAPOT'
    | '421 MISDIRECTED_REQUEST'
    | '422 UNPROCESSABLE_CONTENT'
    | '422 UNPROCESSABLE_ENTITY'
    | '423 LOCKED'
    | '424 FAILED_DEPENDENCY'
    | '425 TOO_EARLY'
    | '426 UPGRADE_REQUIRED'
    | '428 PRECONDITION_REQUIRED'
    | '429 TOO_MANY_REQUESTS'
    | '431 REQUEST_HEADER_FIELDS_TOO_LARGE'
    | '451 UNAVAILABLE_FOR_LEGAL_REASONS'
    | '500 INTERNAL_SERVER_ERROR'
    | '501 NOT_IMPLEMENTED'
    | '502 BAD_GATEWAY'
    | '503 SERVICE_UNAVAILABLE'
    | '504 GATEWAY_TIMEOUT'
    | '505 HTTP_VERSION_NOT_SUPPORTED'
    | '506 VARIANT_ALSO_NEGOTIATES'
    | '507 INSUFFICIENT_STORAGE'
    | '508 LOOP_DETECTED'
    | '509 BANDWIDTH_LIMIT_EXCEEDED'
    | '510 NOT_EXTENDED'
    | '511 NETWORK_AUTHENTICATION_REQUIRED';
  message: string;
  errorCode?: string;
  data: GetMockInterviewProductsResponse;
}

export interface GetMockInterviewProductItemResponse {
  id: number;
  productCode: 'ONE_TIME' | 'FREE_ONE_TIME';
  title: string;
  mockInterviewCount: number;
  regularPrice: number;
  paymentPrice: number;
}

export interface GetMockInterviewProductsResponse {
  items: GetMockInterviewProductItemResponse[];
}

export interface ApiResponseGetMockInterviewOrderResponse {
  statusCode:
    | '100 CONTINUE'
    | '101 SWITCHING_PROTOCOLS'
    | '102 PROCESSING'
    | '103 EARLY_HINTS'
    | '200 OK'
    | '201 CREATED'
    | '202 ACCEPTED'
    | '203 NON_AUTHORITATIVE_INFORMATION'
    | '204 NO_CONTENT'
    | '205 RESET_CONTENT'
    | '206 PARTIAL_CONTENT'
    | '207 MULTI_STATUS'
    | '208 ALREADY_REPORTED'
    | '226 IM_USED'
    | '300 MULTIPLE_CHOICES'
    | '301 MOVED_PERMANENTLY'
    | '302 FOUND'
    | '303 SEE_OTHER'
    | '304 NOT_MODIFIED'
    | '307 TEMPORARY_REDIRECT'
    | '308 PERMANENT_REDIRECT'
    | '400 BAD_REQUEST'
    | '401 UNAUTHORIZED'
    | '402 PAYMENT_REQUIRED'
    | '403 FORBIDDEN'
    | '404 NOT_FOUND'
    | '405 METHOD_NOT_ALLOWED'
    | '406 NOT_ACCEPTABLE'
    | '407 PROXY_AUTHENTICATION_REQUIRED'
    | '408 REQUEST_TIMEOUT'
    | '409 CONFLICT'
    | '410 GONE'
    | '411 LENGTH_REQUIRED'
    | '412 PRECONDITION_FAILED'
    | '413 CONTENT_TOO_LARGE'
    | '413 PAYLOAD_TOO_LARGE'
    | '414 URI_TOO_LONG'
    | '415 UNSUPPORTED_MEDIA_TYPE'
    | '416 REQUESTED_RANGE_NOT_SATISFIABLE'
    | '417 EXPECTATION_FAILED'
    | '418 I_AM_A_TEAPOT'
    | '421 MISDIRECTED_REQUEST'
    | '422 UNPROCESSABLE_CONTENT'
    | '422 UNPROCESSABLE_ENTITY'
    | '423 LOCKED'
    | '424 FAILED_DEPENDENCY'
    | '425 TOO_EARLY'
    | '426 UPGRADE_REQUIRED'
    | '428 PRECONDITION_REQUIRED'
    | '429 TOO_MANY_REQUESTS'
    | '431 REQUEST_HEADER_FIELDS_TOO_LARGE'
    | '451 UNAVAILABLE_FOR_LEGAL_REASONS'
    | '500 INTERNAL_SERVER_ERROR'
    | '501 NOT_IMPLEMENTED'
    | '502 BAD_GATEWAY'
    | '503 SERVICE_UNAVAILABLE'
    | '504 GATEWAY_TIMEOUT'
    | '505 HTTP_VERSION_NOT_SUPPORTED'
    | '506 VARIANT_ALSO_NEGOTIATES'
    | '507 INSUFFICIENT_STORAGE'
    | '508 LOOP_DETECTED'
    | '509 BANDWIDTH_LIMIT_EXCEEDED'
    | '510 NOT_EXTENDED'
    | '511 NETWORK_AUTHENTICATION_REQUIRED';
  message: string;
  errorCode?: string;
  data: GetMockInterviewOrderResponse;
}

export interface GetMockInterviewOrderItemResponse {
  id: number;
  title: string;
  status: 'CANCEL' | 'COMPLETE' | 'DEPOSIT_WAIT';
  regularPrice: number;
  paymentPrice: number;
  krwRegularPrice: number;
  krwPaymentPrice: number;
  isRefundable: boolean;
  mockInterviewProduct: MockInterviewProductResponse;
  vouchers: MockInterviewVoucherResponse[];
}

export interface GetMockInterviewOrderResponse {
  id: number;
  userId: number;
  status:
    | 'CANCEL_DELETE'
    | 'MENTEE_CANCEL'
    | 'PARTIAL_CANCEL'
    | 'CANCEL_NOT_ENOUGH_PROGRESS'
    | 'GROUP_PAYMENT_CANCEL'
    | 'PAYMENT_CANCEL'
    | 'ORDER_FORM'
    | 'CONFLICT_CANCEL'
    | 'AUTO_CANCEL'
    | 'PAYMENT_READY'
    | 'TOTAL_CANCEL'
    | 'PAYMENT_FAIL'
    | 'PAYMENT_STOP'
    | 'CODE_ORDER'
    | 'ORDER_CANCEL'
    | 'PAYMENT_COMPLETE'
    | 'PAYMENT_COMPLETE_PROCESSING'
    | 'ADMIN_CANCEL'
    | 'MENTOR_CANCEL'
    | 'DEPOSIT_WAIT'
    | 'WC_COMPLETED'
    | 'MANUAL_DELETE'
    | 'REFUND_REQUEST';
  paymentMethodType?:
    | 'APPLEPAY'
    | 'BANK_TRANSFER'
    | 'CARD'
    | 'INFPAY'
    | 'KAKAOPAY'
    | 'NAVERPAY'
    | 'TOSSPAY'
    | 'PAYCO';
  regularPrice: number;
  paymentPrice: number;
  discountPrice: number;
  point: number;
  receiptUrl: string;
  isReceiptAvailable: boolean;
  canGetInvoice: boolean;
  isOldDataWithNoReceipt: boolean;
  paymentChannel: string;
  bank?: string;
  currency: string;
  timezone: string;
  paidAtUTC?: string;
  createdAtUTC: string;
  isGlobalPayment: boolean;
  refundedPoint: number;
  refundStatus: 'IDLE';
  items: GetMockInterviewOrderItemResponse[];
}

export interface MockInterviewVoucherResponse {
  id: number;
  type: 'FREE' | 'NO_LIMIT' | 'PAID';
  isUsed: boolean;
  mockInterviewId?: number;
  usedAtUTC?: string;
  expiredAtUTC?: string;
  refundDueAtUTC?: string;
  isDeleted: boolean;
}

/** MockInterviewVoucherStatusFilter */
export const enum MockInterviewVoucherStatusFilter {
  ALL = 'ALL',
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  UNUSED = 'UNUSED',
  CANCELLED = 'CANCELLED',
}

/** MockInterviewProductCode */
export const enum MockInterviewProductCode {
  ONE_TIME = 'ONE_TIME',
  FREE_ONE_TIME = 'FREE_ONE_TIME',
}

/** HttpStatus */
export const enum HttpStatus {
  '100 CONTINUE' = '100 CONTINUE',
  '101 SWITCHING_PROTOCOLS' = '101 SWITCHING_PROTOCOLS',
  '102 PROCESSING' = '102 PROCESSING',
  '103 EARLY_HINTS' = '103 EARLY_HINTS',
  '200 OK' = '200 OK',
  '201 CREATED' = '201 CREATED',
  '202 ACCEPTED' = '202 ACCEPTED',
  '203 NON_AUTHORITATIVE_INFORMATION' = '203 NON_AUTHORITATIVE_INFORMATION',
  '204 NO_CONTENT' = '204 NO_CONTENT',
  '205 RESET_CONTENT' = '205 RESET_CONTENT',
  '206 PARTIAL_CONTENT' = '206 PARTIAL_CONTENT',
  '207 MULTI_STATUS' = '207 MULTI_STATUS',
  '208 ALREADY_REPORTED' = '208 ALREADY_REPORTED',
  '226 IM_USED' = '226 IM_USED',
  '300 MULTIPLE_CHOICES' = '300 MULTIPLE_CHOICES',
  '301 MOVED_PERMANENTLY' = '301 MOVED_PERMANENTLY',
  '302 FOUND' = '302 FOUND',
  '303 SEE_OTHER' = '303 SEE_OTHER',
  '304 NOT_MODIFIED' = '304 NOT_MODIFIED',
  '307 TEMPORARY_REDIRECT' = '307 TEMPORARY_REDIRECT',
  '308 PERMANENT_REDIRECT' = '308 PERMANENT_REDIRECT',
  '400 BAD_REQUEST' = '400 BAD_REQUEST',
  '401 UNAUTHORIZED' = '401 UNAUTHORIZED',
  '402 PAYMENT_REQUIRED' = '402 PAYMENT_REQUIRED',
  '403 FORBIDDEN' = '403 FORBIDDEN',
  '404 NOT_FOUND' = '404 NOT_FOUND',
  '405 METHOD_NOT_ALLOWED' = '405 METHOD_NOT_ALLOWED',
  '406 NOT_ACCEPTABLE' = '406 NOT_ACCEPTABLE',
  '407 PROXY_AUTHENTICATION_REQUIRED' = '407 PROXY_AUTHENTICATION_REQUIRED',
  '408 REQUEST_TIMEOUT' = '408 REQUEST_TIMEOUT',
  '409 CONFLICT' = '409 CONFLICT',
  '410 GONE' = '410 GONE',
  '411 LENGTH_REQUIRED' = '411 LENGTH_REQUIRED',
  '412 PRECONDITION_FAILED' = '412 PRECONDITION_FAILED',
  '413 CONTENT_TOO_LARGE' = '413 CONTENT_TOO_LARGE',
  '413 PAYLOAD_TOO_LARGE' = '413 PAYLOAD_TOO_LARGE',
  '414 URI_TOO_LONG' = '414 URI_TOO_LONG',
  '415 UNSUPPORTED_MEDIA_TYPE' = '415 UNSUPPORTED_MEDIA_TYPE',
  '416 REQUESTED_RANGE_NOT_SATISFIABLE' = '416 REQUESTED_RANGE_NOT_SATISFIABLE',
  '417 EXPECTATION_FAILED' = '417 EXPECTATION_FAILED',
  '418 I_AM_A_TEAPOT' = '418 I_AM_A_TEAPOT',
  '421 MISDIRECTED_REQUEST' = '421 MISDIRECTED_REQUEST',
  '422 UNPROCESSABLE_CONTENT' = '422 UNPROCESSABLE_CONTENT',
  '422 UNPROCESSABLE_ENTITY' = '422 UNPROCESSABLE_ENTITY',
  '423 LOCKED' = '423 LOCKED',
  '424 FAILED_DEPENDENCY' = '424 FAILED_DEPENDENCY',
  '425 TOO_EARLY' = '425 TOO_EARLY',
  '426 UPGRADE_REQUIRED' = '426 UPGRADE_REQUIRED',
  '428 PRECONDITION_REQUIRED' = '428 PRECONDITION_REQUIRED',
  '429 TOO_MANY_REQUESTS' = '429 TOO_MANY_REQUESTS',
  '431 REQUEST_HEADER_FIELDS_TOO_LARGE' = '431 REQUEST_HEADER_FIELDS_TOO_LARGE',
  '451 UNAVAILABLE_FOR_LEGAL_REASONS' = '451 UNAVAILABLE_FOR_LEGAL_REASONS',
  '500 INTERNAL_SERVER_ERROR' = '500 INTERNAL_SERVER_ERROR',
  '501 NOT_IMPLEMENTED' = '501 NOT_IMPLEMENTED',
  '502 BAD_GATEWAY' = '502 BAD_GATEWAY',
  '503 SERVICE_UNAVAILABLE' = '503 SERVICE_UNAVAILABLE',
  '504 GATEWAY_TIMEOUT' = '504 GATEWAY_TIMEOUT',
  '505 HTTP_VERSION_NOT_SUPPORTED' = '505 HTTP_VERSION_NOT_SUPPORTED',
  '506 VARIANT_ALSO_NEGOTIATES' = '506 VARIANT_ALSO_NEGOTIATES',
  '507 INSUFFICIENT_STORAGE' = '507 INSUFFICIENT_STORAGE',
  '508 LOOP_DETECTED' = '508 LOOP_DETECTED',
  '509 BANDWIDTH_LIMIT_EXCEEDED' = '509 BANDWIDTH_LIMIT_EXCEEDED',
  '510 NOT_EXTENDED' = '510 NOT_EXTENDED',
  '511 NETWORK_AUTHENTICATION_REQUIRED' = '511 NETWORK_AUTHENTICATION_REQUIRED',
}

/** MockInterviewOrderCancelReason */
export const enum MockInterviewOrderCancelReason {
  PAYMENT_METHOD_CHANGE = 'PAYMENT_METHOD_CHANGE',
  DISSATISFACTION = 'DISSATISFACTION',
  DISCOUNT = 'DISCOUNT',
  CHANGE_MIND = 'CHANGE_MIND',
  ETC = 'ETC',
}

/** OrderPgType */
export const enum OrderPgType {
  TOSS = 'TOSS',
}

/** OrderPaymentMethodType */
export const enum OrderPaymentMethodType {
  APPLEPAY = 'APPLEPAY',
  BANK_TRANSFER = 'BANK_TRANSFER',
  CARD = 'CARD',
  INFPAY = 'INFPAY',
  KAKAOPAY = 'KAKAOPAY',
  NAVERPAY = 'NAVERPAY',
  TOSSPAY = 'TOSSPAY',
  PAYCO = 'PAYCO',
}

/** MockInterviewStatus */
export const enum MockInterviewStatus {
  INITIALIZED = 'INITIALIZED',
  SUBMIT = 'SUBMIT',
  PREPARING = 'PREPARING',
  READY = 'READY',
  ON_AIR = 'ON_AIR',
  FINISHED = 'FINISHED',
  REPORT_GENERATING = 'REPORT_GENERATING',
  REPORT_GENERATED = 'REPORT_GENERATED',
}

/** InterviewerPersonaType */
export const enum InterviewerPersonaType {
  DEFAULT = 'DEFAULT',
  CALM_ANALYST = 'CALM_ANALYST',
  RELAXED_EXECUTIVE = 'RELAXED_EXECUTIVE',
  CURIOUS_LEAD = 'CURIOUS_LEAD',
  DIRECT_PRACTITIONER = 'DIRECT_PRACTITIONER',
  HYANGRO = 'HYANGRO',
}

/** MockInterviewVoucherStatus */
export const enum MockInterviewVoucherStatus {
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  UNUSED = 'UNUSED',
  CANCELLED = 'CANCELLED',
}

/** MockInterviewVoucherType */
export const enum MockInterviewVoucherType {
  FREE = 'FREE',
  NO_LIMIT = 'NO_LIMIT',
  PAID = 'PAID',
}

/** MockInterviewVoucherUserType */
export const enum MockInterviewVoucherUserType {
  NO_LIMIT = 'NO_LIMIT',
  FREE_AVAILABLE = 'FREE_AVAILABLE',
  HAS_VOUCHER = 'HAS_VOUCHER',
  NO_VOUCHER = 'NO_VOUCHER',
}

/** OrderItemStatus */
export const enum OrderItemStatus {
  CANCEL = 'CANCEL',
  COMPLETE = 'COMPLETE',
  DEPOSIT_WAIT = 'DEPOSIT_WAIT',
}

/** OrderStatus */
export const enum OrderStatus {
  CANCEL_DELETE = 'CANCEL_DELETE',
  MENTEE_CANCEL = 'MENTEE_CANCEL',
  PARTIAL_CANCEL = 'PARTIAL_CANCEL',
  CANCEL_NOT_ENOUGH_PROGRESS = 'CANCEL_NOT_ENOUGH_PROGRESS',
  GROUP_PAYMENT_CANCEL = 'GROUP_PAYMENT_CANCEL',
  PAYMENT_CANCEL = 'PAYMENT_CANCEL',
  ORDER_FORM = 'ORDER_FORM',
  CONFLICT_CANCEL = 'CONFLICT_CANCEL',
  AUTO_CANCEL = 'AUTO_CANCEL',
  PAYMENT_READY = 'PAYMENT_READY',
  TOTAL_CANCEL = 'TOTAL_CANCEL',
  PAYMENT_FAIL = 'PAYMENT_FAIL',
  PAYMENT_STOP = 'PAYMENT_STOP',
  CODE_ORDER = 'CODE_ORDER',
  ORDER_CANCEL = 'ORDER_CANCEL',
  PAYMENT_COMPLETE = 'PAYMENT_COMPLETE',
  PAYMENT_COMPLETE_PROCESSING = 'PAYMENT_COMPLETE_PROCESSING',
  ADMIN_CANCEL = 'ADMIN_CANCEL',
  MENTOR_CANCEL = 'MENTOR_CANCEL',
  DEPOSIT_WAIT = 'DEPOSIT_WAIT',
  WC_COMPLETED = 'WC_COMPLETED',
  MANUAL_DELETE = 'MANUAL_DELETE',
  REFUND_REQUEST = 'REFUND_REQUEST',
}

/** MockInterviewOrderRefundStatus */
export const enum MockInterviewOrderRefundStatus {
  IDLE = 'IDLE',
}
